import {
  DeleteOutlined,
  PaperClipOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import {
  Button,
  Col,
  Form,
  Input,
  List,
  Popconfirm,
  Row,
  Tooltip,
  Typography,
  Upload,
  UploadFile,
} from 'antd'
import { uniqueId } from 'lodash'
import { DocumentData } from 'Modules'
import { Link } from 'react-router-dom'

import { FEATURE } from 'constants/feature'
import { ALLOWED_MIME_TYPES_PDF } from 'constants/mime-type'
import { usePermission } from 'hooks/use-permission'

const DocumentUploader = ({
  defaultFile,
  disabled,
  setFile,
}: {
  disabled: boolean
  defaultFile?: {
    uid: string
    name: string
    url: string
  }
  setFile: (document: UploadFile) => void
}) => {
  const previewFile = (file: UploadFile) => {
    let url = file.url
    if (file.originFileObj) {
      url = URL.createObjectURL(file.originFileObj || file.url)
    }

    if (url) window.open(url)
  }

  return (
    <Upload
      maxCount={1}
      defaultFileList={defaultFile ? [defaultFile] : undefined}
      disabled={disabled}
      beforeUpload={(file) => {
        setFile(file)
        return false
      }}
      itemRender={(__, file, _) => {
        if (!file.url && !file.originFileObj) return null

        return (
          <div style={{ marginTop: 4 }}>
            <PaperClipOutlined />
            <Link to="" onClick={() => previewFile(file)}>
              <Typography.Text ellipsis style={{ maxWidth: 300, marginRight: 8, color: 'inherit' }}>
                {file.name}
              </Typography.Text>
            </Link>
          </div>
        )
      }}
      accept={ALLOWED_MIME_TYPES_PDF.join(',')}
    >
      <Button disabled={disabled} icon={<UploadOutlined />}>
        Upload Document
      </Button>
    </Upload>
  )
}

const Documents = ({
  edit,
  documentDetails,
  setDocumentDetails,
}: {
  edit: boolean
  documentDetails: DocumentData
  setDocumentDetails: (data: DocumentData | any) => void
}) => {
  const { canCRUD } = usePermission()
  const canEdit = canCRUD(FEATURE.PTO)

  const handleFieldChange = <T extends keyof DocumentData>(
    field: T,
    index: number,
    key: keyof DocumentData[T][number],
    value: string | UploadFile
  ) => {
    setDocumentDetails((prevDetails: any) => {
      const updatedField = [...prevDetails[field]] // Remove type assertion
      updatedField[index] = { ...updatedField[index], [key]: value }

      return { ...prevDetails, [field]: updatedField }
    })
  }

  const handleDeleteItem = <T extends keyof DocumentData>(field: T, index: number) => {
    setDocumentDetails((prevDetails: any) => {
      // Create a shallow copy of the array for the given field
      const updatedField = [...(prevDetails[field] || [])]
      updatedField.splice(index, 1) // Remove the item at the specified index
      return { ...prevDetails, [field]: updatedField }
    })
  }

  const addNewItem = <T extends keyof DocumentData>(
    field: T,
    defaultValue: DocumentData[T][number]
  ) => {
    setDocumentDetails((prevDetails: any) => {
      // Create a shallow copy of the array for the given field
      const updatedField = [...(prevDetails[field] || []), defaultValue]
      return { ...prevDetails, [field]: updatedField }
    })
  }

  return (
    <fieldset>
      <legend>Document Details</legend>
      <>
        <List
          dataSource={documentDetails.documentDetail}
          renderItem={(doc, index) => (
            <List.Item
              actions={[
                <Popconfirm
                  key={`${doc.name}-${doc.link}`}
                  title="Are you sure to delete this document?"
                  onConfirm={() => handleDeleteItem('documentDetail', index)}
                  okText="Yes"
                  cancelText="No"
                >
                  <DeleteOutlined
                    style={
                      edit
                        ? {}
                        : {
                            pointerEvents: 'none',
                            cursor: 'not-allowed',
                          }
                    }
                    key="delete"
                  />
                </Popconfirm>,
              ]}
            >
              <Row gutter={16} style={{ width: '100%' }}>
                <Col span={12}>
                  <Form.Item
                    label={
                      <>
                        Name{' '}
                        <Tooltip
                          title={
                            <div className="tooltip-box">
                              <p className="tooltip-text">
                                This is the Document name. Please enter your chosen name as text.
                              </p>
                            </div>
                          }
                          placement="right"
                        >
                          <QuestionCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </>
                    }
                    // name={["documentDetail", index, "name"]}
                    rules={[
                      {
                        required: true,
                        message: 'Please enter a document name',
                      },
                      {
                        min: 3,
                        max: 50,
                        message: 'Input must be between 3 and 50 characters',
                      },
                    ]}
                  >
                    <Input
                      style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                      disabled={!edit}
                      value={doc.name}
                      placeholder="Enter document name"
                      onChange={(e) =>
                        handleFieldChange('documentDetail', index, 'name', e.target.value)
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="document"
                    label={
                      <>
                        Upload document
                        <Tooltip
                          title={
                            <div className="tooltip-box">
                              <p className="tooltip-text">
                                This is the document file. Please enter your chosen document.
                              </p>
                            </div>
                          }
                          placement="right"
                        >
                          <QuestionCircleOutlined className="tooltip-icon" />
                        </Tooltip>
                      </>
                    }
                  >
                    <DocumentUploader
                      disabled={!edit}
                      defaultFile={
                        doc.link
                          ? {
                              uid: uniqueId(),
                              name: doc.link,
                              url: doc.link,
                            }
                          : undefined
                      }
                      setFile={(document) =>
                        handleFieldChange('documentDetail', index, 'document', document)
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
            </List.Item>
          )}
        />
        <Button
          type="dashed"
          onClick={() =>
            addNewItem('documentDetail', {
              name: '',
              link: '',
              document: undefined,
            })
          }
          style={{ width: '100%', marginBottom: 16 }}
          disabled={!canEdit || !edit}
        >
          <PlusOutlined /> Add Document
        </Button>
      </>
    </fieldset>
  )
}

export default Documents
